<template>
  <div v-if="question && data" class="overflow-x-auto relative pb-4 h-full bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('questionnaire.title')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.title"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="getInputErrors('title')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('questionnaire.description')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.description"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('question.dataType')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownDataTypes"
            :selected-item="selectedDataType"
            flat
            :mode="mode"
            size="sm"
            name="dataType"
            clearable
            show-error-as-tooltip
            :error="getInputErrors('dataType')"
            @update:selected="handleDataTypeSelection" />
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          v-if="data.mwDataType && data.mwDataType.code <= 5"
          :name="$t('question.halo')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.halo"
            color="base"
            flat
            type="number"
            show-error-as-tooltip
            :error="getInputErrors('halo')"
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          v-if="data.mwDataType && data.mwDataType?.code <= 5"
          :name="$t('question.lalo')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.lalo"
            color="base"
            flat
            type="number"
            show-error-as-tooltip
            :error="getInputErrors('lalo')"
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('question.sort')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.sort"
            type="number"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="getInputErrors('sort')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('question.lastVisitData')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus
            v-if="mode == 'view'"
            color="blue"
            :item="data.isIncludeLastVisitData"
            text-true="Include"
            text-false="Not Included"></BaseTableCellStatus>
          <BaseToggle
            v-else
            :label="$t('question.lastVisitData')"
            name="isIncludeLastVisitData"
            v-model="data.isIncludeLastVisitData" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('question.nullValues')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus
            v-if="mode == 'view'"
            color="blue"
            :item="data.allowNull"
            text-true="Allow"
            text-false="Not Allowed"></BaseTableCellStatus>
          <BaseToggle
            v-else
            :label="$t('question.nullValues')"
            name="nullValues"
            v-model="data.allowNull" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          v-if="selectedDataType && selectedDataType.name === MWDataTypes.BOOLEAN"
          :name="$t('question.image')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout"
          @dragstart.prevent
          @drop.prevent>
          <ImageUpload
            v-if="mode == 'edit'"
            :image="image"
            class="my-4"
            @image-changed="onPhotoChange" />
          <span v-else><img v-if="image" :src="image" /></span>
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isActive')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus v-if="mode == 'view'" :item="data.isActive ? data.isActive : 0" />
          <BaseToggle
            v-else
            :label="$t('baseObject.isActive')"
            name="isActive"
            v-model="data.isActive" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isFromManager')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck :item="data.isFromManager ? data.isFromManager : false" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.createUser?.name || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createdDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.createdDate ? isoDateToFullDate(data.createdDate) : '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.lastUpdate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.lastUpdate ? isoDateToFullDate(data.lastUpdate) : '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.updateUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.updateUser?.name || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.boid')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.boid || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.exportDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.exportedDate ? isoDateToFullDate(data.exportedDate) : '-' }}
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseToggle from '../common/BaseToggle.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseTableCellCheck from '../common/BaseTableCellCheck.vue';
import ImageUpload from '../common/ImageUpload.vue';

import { type Question } from '@/types/Questionnaires';
import type { SelectDropdownItem } from '@/types/Base';

import { convertBlobToBase64 } from '@/utils/image';
import { isoDateToFullDate } from '@/utils/date';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, minValue, integer } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { MWDataTypes } from '@/utils/constants';
import { useLookupsStore } from '@/stores/lookups';

import { storeToRefs } from 'pinia';

const { t: $t } = useI18n({ useScope: 'global' });

const props = defineProps<{
  question: Question | undefined;
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
}>();

const data = defineModel<Question>();

const { dropdownDataTypes, dataTypes } = storeToRefs(useLookupsStore());
const { getDataTypesLookup } = useLookupsStore();

onMounted(async () => {
  await getDataTypesLookup();
  await initDropdowns();
});

const image = computed(() => {
  if (!data.value) return;
  if (data.value.image && !data.value.image.includes('data:image'))
    return `data:image/jpeg;base64,${data.value?.image}`;
  return data.value.image;
});

const selectedDataType = ref<SelectDropdownItem | null>(null);

function handleDataTypeSelection(item: SelectDropdownItem) {
  selectedDataType.value = item ? item : null;
  if (data.value) {
    data.value.mwDataTypeId = item ? (item.value as string) : '';
    if (item) {
      const dataType = dataTypes.value.find((x) => x.id === item.value);
      if (dataType) {
        data.value.mwDataType = dataType;
        if (dataType.name !== MWDataTypes.BOOLEAN) data.value.image = undefined;
        if (dataType.code > 5) {
          data.value.halo = undefined;
          data.value.lalo = undefined;
        }
      }
    }
  }
}

async function initDropdowns() {
  if (!data.value) return;

  const dataTypeValue = dropdownDataTypes.value.find(
    (item) => item.value == data.value?.mwDataTypeId
  );
  if (dataTypeValue) selectedDataType.value = dataTypeValue;
}

const isModified = ref<boolean>(false);

watch(
  () => data.value,
  (newData) => {
    validationModel.value.title = newData?.title || '';
    validationModel.value.dataType = newData?.mwDataTypeId || '';
    validationModel.value.sort =
      newData && newData.sort != undefined && newData != null ? newData.sort : undefined;
    validationModel.value.halo =
      newData && newData.halo != undefined && newData != null ? newData.halo : undefined;
    validationModel.value.lalo =
      newData && newData.lalo != undefined && newData != null ? newData.lalo : undefined;
    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.question);
    isModified.value = valueChanged;
  },
  { deep: true }
);

const validationModel = ref({
  title: data.value?.title || '',
  dataType: data.value?.mwDataTypeId || '',
  image: data.value?.image || null,
  sort:
    data.value && data.value.sort != undefined && data.value?.sort != null
      ? data.value.sort
      : undefined,
  halo:
    data.value && data.value.halo != undefined && data.value?.halo != null
      ? data.value.halo
      : undefined,
  lalo:
    data.value && data.value.lalo != undefined && data.value?.lalo != null
      ? data.value.lalo
      : undefined,
});

const rules = computed(() => ({
  title: {
    required: helpers.withMessage($t('messages.errors.required'), required),
  },
  sort: {
    required: helpers.withMessage($t('messages.errors.required'), required),
    minValue: helpers.withMessage($t('messages.errors.invalidValue'), minValue(0)),
    integer: helpers.withMessage($t('messages.errors.invalidValue'), integer),
  },
  image: {},
  dataType: {
    required: helpers.withMessage($t('messages.errors.required'), required),
    required_value: helpers.withMessage(
      "Question's answer should be boolean for questions containing images",
      (value) => {
        if (data.value && data.value.image != null) {
          return (
            value === dropdownDataTypes.value.find((x) => x.name == MWDataTypes.BOOLEAN)?.value ||
            false
          );
        }
        return true;
      }
    ),
  },
  halo: {
    minValue: helpers.withMessage($t('messages.errors.nonNegative'), minValue(0)),
    greaterOrEqualLalo: helpers.withMessage(
      $t('messages.errors.maxGraterThanMin'),
      (value, siblings) => value == null || siblings.lalo == null || +value >= +siblings.lalo
    ),
  },
  lalo: {
    minValue: helpers.withMessage($t('messages.errors.nonNegative'), minValue(0)),
    lessOrEqualHalo: helpers.withMessage(
      $t('messages.errors.minSmallerThanMax'),
      (value, siblings) => value == null || siblings.halo == null || +value <= +siblings.halo
    ),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function getInputErrors(type: 'title' | 'dataType' | 'halo' | 'lalo' | 'sort') {
  if ($v.value[type].required && $v.value[type].required.$invalid)
    return $v.value[type].required.$message;
  else if ($v.value[type].required_value && $v.value[type].required_value.$invalid)
    return $v.value[type].required_value.$message;
  else if ($v.value[type].minValue && $v.value[type].minValue.$invalid)
    return $v.value[type].minValue.$message;
  else if ($v.value[type].integer && $v.value[type].integer.$invalid)
    return $v.value[type].integer.$message;
  else if ($v.value[type].lessOrEqualHalo && $v.value[type].lessOrEqualHalo.$invalid)
    return $v.value[type].lessOrEqualHalo.$message;
  else if ($v.value[type].greaterOrEqualLalo && $v.value[type].greaterOrEqualLalo.$invalid)
    return $v.value[type].greaterOrEqualLalo.$message;
  else return '';
}

function onPhotoChange(image: string) {
  if (!data.value) return;
  if (!image) data.value.image = undefined;
  if (image && image.startsWith('blob')) {
    convertBlobToBase64(image)
      .then((base64Image) => {
        if (data.value)
          data.value.image = base64Image.replace(/^data:image\/[a-zA-Z]+;base64,/, '');
      })
      .catch((error) => {
        console.error('Error converting blob to Base64:', error);
      });
  } else if (image && image.startsWith('data:image/,') && image.endsWith(';base64,')) {
    data.value.image = image.replace(/^data:image\/[a-zA-Z]+;base64,/, '');
  }
}
</script>
